import React, { useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
import { Pressable, Flex, Inline } from '@atlaskit/primitives/compiled';

import { fg } from '@confluence/feature-gating';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { usePageContentId } from '@confluence/page-context';

const styles = cssMap({
	addCommentButtonStyle: {
		display: 'flex',
		minHeight: '32px',
		padding: token('space.0'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: token('radius.small'),
		font: token('font.body'),
		color: token('color.text.subtle'),
	},
	enabledStyle: {
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			cursor: 'pointer',
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	disabledStyle: {
		backgroundColor: token('color.background.disabled'),
		cursor: 'not-allowed',
		color: token('color.text.disabled'),
	},
	fullWidthStyle: {
		width: '100%',
	},
});

const i18n = defineMessages({
	addCommentPromptText: {
		id: 'comments-common.add-comment-prompt.text',
		defaultMessage: 'Add a comment',
		description: 'Label for the add comment prompt',
	},
	addCommentPromptAriaLabel: {
		id: 'comments-common.add-comment-prompt.aria-label',
		defaultMessage: 'Add a general comment',
		description: 'The aria label for the add general comment entry prompt field',
	},
});

export const AddCommentPromptButton = ({
	onClick,
	autofocusRef,
	shouldFitContainer,
	testId,
}: {
	onClick: () => void;
	autofocusRef: { current: boolean | null };
	shouldFitContainer?: boolean;
	testId?: string;
}) => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();

	const { canAddComments } = useAddCommentPermissionCheck(contentId || '');

	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (autofocusRef.current) {
			(buttonRef.current as HTMLElement)?.focus({
				preventScroll: true,
			});
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	const allowAddComment = fg('confluence_frontend_content_wrapper') ? canAddComments : true;
	return (
		<Pressable
			xcss={cx(
				styles.addCommentButtonStyle,
				!allowAddComment ? styles.disabledStyle : styles.enabledStyle,
				shouldFitContainer && styles.fullWidthStyle,
			)}
			isDisabled={!allowAddComment}
			onClick={onClick}
			testId={testId}
			ref={buttonRef}
			aria-label={formatMessage(i18n.addCommentPromptAriaLabel)}
		>
			<Flex alignItems="center" gap="space.100">
				<CommentAddIcon label={formatMessage(i18n.addCommentPromptText)} />
				<Inline>{formatMessage(i18n.addCommentPromptText)}</Inline>
			</Flex>
		</Pressable>
	);
};
